import { useState, type ReactNode } from 'react';
import { FileText, Flag, MessageSquare, Phone, PhoneIncoming, ShieldOff, Zap } from 'lucide-react';
import { sourceLabel } from '../../lib/leads/sourceLabels';
import { callTitle, firstNameOf, speedChip } from '../../lib/leads/leadView';
import { groupThread, threadTime } from '../../lib/leads/threadView';
import { formatDuration, speakerNote } from '../../lib/speedToLead/callLogView';
import { segmentsFromOffset } from '../../lib/speedToLead/callMedia';
import { LEAD_OUTCOME_LABELS, type LeadOutcome } from '../../types/leadOutcome.js';
import { ConversationSummaryCard } from './ConversationSummaryCard';
import { RecordingBar } from './RecordingBar';
import { leadEventText } from './LeadHistoryPanel';
import type { LeadHistoryEntry } from '../../lib/speedToLead/leadHistory';
import { botNameOf } from '../../lib/botName';
import type { CallLogRow } from '../../types/leadCall';

// THE STORY SO FAR (Leads centre v2): the lead's merged history as a vertical timeline, newest first, an icon per event. It reads the
// SAME history page the old panel did (no second read model); only the drawing changed.
//
// ⚠️ A call card has NO outcome control: the lead's ONE outcome is set above ("Where it's at"). It shows the AI summary, the outcome a
// person confirmed (and who), or the AI's suggestion as TEXT. ⚠️ No recording line when there is no recording (never "Not recorded").
// ⚠️ The recording bar fetches its signed URL only when Play is pressed. Nothing here makes a request on render.

export interface LeadStoryProps {
  entries: LeadHistoryEntry[];
  assistantName: string | null;
  leadName: string | null;
  loadPlayUrl: (kind: CallLogRow['kind'], id: string) => Promise<string>;
  /** The text composer (Part C), placed at the bottom of the text conversation card. Absent = no composer. */
  composer?: ReactNode;
  /** The club's timezone, so every time on the story reads in the club's own clock (the viewer's when absent). */
  timeZone?: string | null;
  className?: string;
}

const when = (iso: string, timeZone?: string | null): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  try {
    return d.toLocaleString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', ...(timeZone ? { timeZone } : {}) });
  } catch {
    return d.toLocaleString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
  }
};

function Node({ icon, children, testId, quiet = false, last = false, kind }: { icon: ReactNode; children: ReactNode; testId: string; quiet?: boolean; last?: boolean; kind: 'call' | 'text' | 'enquiry' | 'event' }) {
  return (
    <li className="kc-node relative flex gap-3 pb-4" data-testid={testId}>
      {/* The vertical line runs from under this icon to the next one; the portal look re-positions it for its 36px icons. */}
      {!last && <span aria-hidden className="kc-line absolute bottom-0 left-[13px] top-[30px] w-px bg-border" />}
      <span aria-hidden data-kind={kind} className="kc-node-icon relative z-10 mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border bg-card text-muted-foreground">
        {icon}
      </span>
      <div className={`kc-node-body min-w-0 flex-1 ${quiet ? 'pt-1 text-sm text-muted-foreground' : ''}`} data-quiet={quiet ? 'true' : undefined}>{children}</div>
    </li>
  );
}

function CallCard({ row, leadFirst, loadPlayUrl, timeZone }: { row: CallLogRow; leadFirst: string; loadPlayUrl: LeadStoryProps['loadPlayUrl']; timeZone?: string | null }) {
  const [showTranscript, setShowTranscript] = useState(false);
  const chip = speedChip(row);
  const suggested = row.summary?.status === 'ready' ? row.summary.suggestedOutcome : null;
  const personConfirmed = row.outcomeRecord?.source === 'person';
  const segments = row.transcript?.status === 'ready' && row.transcript.text ? segmentsFromOffset(row.transcript.segments ?? [{ channel: 0, text: row.transcript.text, label: 'Unclear' }], row.recordingOffsetSeconds) : [];
  return (
    <div className="kc-card space-y-2 rounded border border-border bg-card p-3" data-testid="story-call">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-medium text-foreground" data-testid="story-call-title">{callTitle(row, leadFirst)}</p>
        <p className="kc-label text-xs text-muted-foreground">
          {when(row.at, timeZone)}
          {row.durationSeconds !== null ? ` · ${formatDuration(row.durationSeconds)}` : ''}
        </p>
      </div>
      <p className="text-sm text-muted-foreground" data-testid="story-call-result">{row.outcome}</p>
      {chip && (
        // The board's dark chip, then the facts as plain text (not an all-caps strip).
        <p className="flex flex-wrap items-center gap-2 text-sm" data-testid="story-speed-chip">
          <span className="kc-speed-chip kc-pill inline-flex items-center gap-1 rounded-full bg-foreground px-2.5 py-0.5 text-xs font-medium text-background">
            <Zap size={12} strokeWidth={1.5} aria-hidden className="kc-speed-bolt" />
            Speed to Lead
          </span>
          <span className="text-foreground" data-testid="story-speed-text">{chip}</span>
        </p>
      )}
      {row.recording?.status === 'stored' && <RecordingBar kind={row.kind} id={row.id} offsetSeconds={row.recordingOffsetSeconds} durationSeconds={row.durationSeconds} loadPlayUrl={loadPlayUrl} />}
      {row.summary?.status === 'ready' && row.summary.text && (
        <div className="kc-summary space-y-1 rounded-lg bg-green-50 p-2 text-sm" data-testid="story-call-summary">
          <p data-testid="story-call-summary-text">{row.summary.text}</p>
          {row.summary.nextStep && <p className="text-muted-foreground">Next step: {row.summary.nextStep}</p>}
          <p className="text-xs text-muted-foreground">Written by AI from the transcript. Check it.</p>
        </div>
      )}
      {row.outcomeRecord && (
        <p className="text-sm" data-testid="story-call-outcome">
          <span className="kc-pill rounded-full border border-border px-2.5 py-0.5 text-xs text-foreground">{row.outcomeRecord.label}</span>{' '}
          <span className="text-muted-foreground">{personConfirmed ? `confirmed${row.outcomeRecord.confirmedByName ? ` by ${row.outcomeRecord.confirmedByName}` : ''}` : 'set automatically'}</span>
        </p>
      )}
      {!personConfirmed && suggested && (
        <p className="text-sm text-muted-foreground" data-testid="call-suggestion-text">
          Suggested by AI: {LEAD_OUTCOME_LABELS[suggested as LeadOutcome]}{row.summary?.confidence ? ` (${row.summary.confidence} confidence)` : ''}
        </p>
      )}
      {segments.length > 0 && (
        <div className="space-y-1" data-testid="story-transcript">
          <button type="button" className="text-sm underline" aria-expanded={showTranscript} onClick={() => setShowTranscript(!showTranscript)} data-testid="story-transcript-toggle">
            {showTranscript ? 'Hide transcript' : `Show transcript (${segments.length} line${segments.length === 1 ? '' : 's'})`}
          </button>
          {showTranscript && (
            <div className="space-y-1 rounded-lg bg-muted p-2 text-sm">
              {segments.map((s, i) => (
                <p key={i}>
                  {row.transcript?.speakerLabels === 'labelled' && <span className="font-medium text-foreground">{s.label}: </span>}
                  {s.text}
                </p>
              ))}
              {row.transcript && speakerNote(row.transcript) && <p className="text-xs text-muted-foreground" data-testid="speaker-note">{speakerNote(row.transcript)}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const SHOWN_MESSAGES = 3;

function ConversationCard({ e, assistantName, composer, timeZone }: { e: Extract<LeadHistoryEntry, { kind: 'conversation' }>; assistantName: string | null; composer?: ReactNode; timeZone?: string | null }) {
  const [all, setAll] = useState(false);
  const real = e.messages;
  const shown = all ? real : real.slice(-SHOWN_MESSAGES);
  // The thread reads oldest first, grouped by who spoke, with one time under each group.
  const items = groupThread(shown);
  return (
    <div className="kc-card space-y-3 rounded border border-border bg-card p-3" data-testid="story-conversation">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-medium text-foreground">Text conversation</p>
        <p className="kc-label text-xs text-muted-foreground">{when(e.at, timeZone)}</p>
      </div>
      <ConversationSummaryCard id={`story-outcome-${e.id}`} summary={e.summary ?? null} current={e.outcome} />
      {real.length > shown.length && (
        <button type="button" className="text-sm underline" onClick={() => setAll(true)} data-testid="story-show-all">Show all {real.length}</button>
      )}
      {/* The framed chat area: its own background, so the thread reads as a thread. */}
      <ul className="kc-thread space-y-2 rounded-[14px] bg-muted/40 p-3.5" aria-label="Text messages" data-testid="story-thread">
        {items.map((item) =>
          item.kind === 'note' ? (
            <li key={item.id} className="kc-sysline px-1 text-center text-xs italic text-muted-foreground" data-testid="history-note" data-from="note">
              {item.text}
            </li>
          ) : (
            <li key={item.key} className={`flex flex-col gap-1 ${item.side === 'lead' ? 'items-start' : 'items-end'}`} data-testid="history-group" data-side={item.side}>
              {item.side === 'person' ? (
                <span className="kc-sender text-xs font-medium text-foreground" data-testid="history-sender">{item.sender}</span>
              ) : (
                <span className="sr-only">{item.side === 'lead' ? 'Lead' : botNameOf(assistantName)}</span>
              )}
              {item.messages.map((m, index) => (
                <span
                  key={m.id}
                  className={`kc-bubble max-w-[85%] rounded-lg px-3 py-1.5 text-foreground ${item.side === 'lead' ? 'bg-muted' : item.side === 'person' ? 'bg-blue-100' : 'bg-primary/15'}`}
                  data-bubble={item.side === 'lead' ? 'lead' : 'assistant'}
                  data-side={item.side}
                  data-tail={index === item.messages.length - 1 ? 'true' : undefined}
                  data-testid="history-message"
                  data-from={item.side === 'lead' ? 'lead' : 'assistant'}
                >
                  {m.text}
                </span>
              ))}
              <span className="kc-label text-[11px] text-muted-foreground" data-testid="history-time">{threadTime(item.at, timeZone)}</span>
            </li>
          ),
        )}
      </ul>
      {composer}
    </div>
  );
}

export function LeadStory({ entries, assistantName, leadName, loadPlayUrl, composer, timeZone, className }: LeadStoryProps) {
  const leadFirst = firstNameOf(leadName);
  // ONE composer, under the NEWEST text conversation (the list is newest first), never one per conversation.
  const composerHome = entries.find((e) => e.kind === 'conversation')?.id ?? null;
  // The enquiry is always last (oldest); everything else is in the order the server merged them.
  return (
    <ol className={`kc-timeline relative ${className ?? ''}`} aria-label="The story so far, newest first" data-testid="lead-story">
      {entries.map((e, index) => {
        const last = index === entries.length - 1;
        switch (e.kind) {
          case 'call':
            return (
              <Node key={`call-${e.id}`} kind="call" icon={e.row.kind === 'inbound' ? <PhoneIncoming size={16} strokeWidth={1.5} /> : <Phone size={16} strokeWidth={1.5} />} testId="history-call" last={last}>
                <CallCard row={e.row} leadFirst={leadFirst} loadPlayUrl={loadPlayUrl} timeZone={timeZone} />
              </Node>
            );
          case 'conversation':
            return (
              <Node key={`conv-${e.id}`} kind="text" icon={<MessageSquare size={16} strokeWidth={1.5} />} testId="history-conversation" last={last}>
                <ConversationCard e={e} assistantName={assistantName} composer={e.id === composerHome ? composer : undefined} timeZone={timeZone} />
              </Node>
            );
          case 'lead_event':
            return (
              <Node key={`le-${e.id}`} kind="event" icon={<Flag size={16} strokeWidth={1.5} />} testId="history-lead-event" quiet last={last}>
                <p>{when(e.at, timeZone)} · {leadEventText(e)}</p>
              </Node>
            );
          case 'event':
            return (
              <Node key={`ev-${e.id}`} kind="event" icon={<ShieldOff size={16} strokeWidth={1.5} />} testId="history-event" quiet last={last}>
                <p>{when(e.at, timeZone)} · {e.event === 'do_not_contact' ? 'Marked do not contact' : e.event === 'opted_out' ? 'Opted out of texts' : 'Opted back in to texts'}{e.note ? ` (${e.note})` : ''}</p>
              </Node>
            );
          case 'intake':
            return (
              <Node key={`in-${e.id}`} kind="enquiry" icon={<FileText size={16} strokeWidth={1.5} />} testId="history-intake" quiet last={last}>
                <p>
                  <span className="font-medium text-foreground">Enquiry received</span>
                  {e.source ? ` · ${sourceLabel(e.source)}` : ''} · {when(e.at, timeZone)}
                </p>
              </Node>
            );
        }
      })}
    </ol>
  );
}
